import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import apiErrorHandler from '../../../api/apiErrorHandler';
import { setLoader } from '../../../redux/actions/api';
import { clearErrors } from '../../../redux/actions/error';
import { RequestApplyControllerService } from '../../../services';
import { REQUEST_REASON_TYPE } from '../../../utils/constants/string';
import { validateModalRequestApplyCompetitorStore } from '../../../utils/helpers/validation';
import Card from '../../common/Card';
import Choice from '../../common/Choice';
import ReactCalender from '../../common/Datepicker';
import Input from '../../common/Input';
import Modal from '../../common/Modal';
import { RequiredBadge } from '../../common/RequiredBadge';

/**
 * 競合店変更・削除依頼ダイアログ
 */
const ModalRequestApplyCompetitorStore = ({ competitorStoreCode, competitorStoreName, competitorChainName, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // 依頼理由
    const [requestReasonType, setRequestReasonType] = useState('RENOVATION');

    // その他入力欄
    const [requestReasonOther, setRequestReasonOther] = useState('');

    // 変更後店名
    const [newStoreName, setNewStoreName] = useState('');

    // 反映希望日
    const [preferredDate, setPreferredDate] = useState(null);

    /**
     * 送信した
     */
    const handleSubmit = async () => {
        dispatch(clearErrors());

        const formData = {
            // 依頼理由
            requestReasonType,

            // その他入力欄
            requestReasonOther,

            // 変更後店名
            newStoreName,

            // 反映希望日
            preferredDate,
        };

        // バリデーション
        const isValid = validateModalRequestApplyCompetitorStore(dispatch, formData);
        if (isValid) return;

        try {
            dispatch(setLoader(true));

            // 競合店変更削除依頼API
            await RequestApplyControllerService.requestCompetitorStore(competitorStoreCode, formData);

            // ダイアログを閉じる
            onClose();
        } catch (error) {
            apiErrorHandler(dispatch, error);
        } finally {
            dispatch(setLoader(false));
        }
    };

    return (
        <Modal
            size="xll"
            isShow={true}
            onClose={onClose}
            isShowIconClose="true"
            title={t('survey-history.request-apply-competitor-store-modal.header')}
            style={{
                minWidth: '1200px !important',
                width: '90%',
                display: 'block',
                position: 'absolute',
                top: '0px',
                right: '0px',
                bottom: '0',
                left: '60px',
                overflow: 'auto',
                overflowY: 'auto',
            }}
            btnList={[
                {
                    name: t('survey-history.request-apply-competitor-store-modal.footer.button.submit'),
                    className: 'btn--primary',
                    onClick: () => handleSubmit(),
                    disabled: false,
                },
            ]}
        >
            <div className="okc-card okc-result-form">
                <Card type="modal_card">
                    <div className="row okc-card__row">
                        {/* 競合店舗名 */}
                        <div className="col-lg-3 mb-3 okc-result-form__title">
                            <p>{t('survey-history.request-apply-competitor-store-modal.competitor-store-name.label')}</p>
                        </div>
                        <div className="col-lg-8 offset-lg-1 mb-3">{competitorStoreName}</div>

                        {/* 依頼理由 */}
                        <div className="col-lg-3 mb-3 okc-result-form__title">
                            <p>{t('survey-history.request-apply-competitor-store-modal.request-reason-type.label')}</p>
                        </div>
                        <div className="col-lg-8 offset-lg-1 mb-3 flex-column">
                            <div className="row w-100">
                                <Choice
                                    column="col-xl-3 col-lg-4 col-md-5"
                                    type="radio"
                                    data={REQUEST_REASON_TYPE}
                                    onChange={(value) => setRequestReasonType(value[0])}
                                    checked={[requestReasonType]}
                                />

                                {/* その他入力欄 */}
                                <Input
                                    placeholder={t('survey-history.request-apply-competitor-store-modal.request-reason-other.label')}
                                    maxLength="60"
                                    value={requestReasonOther}
                                    onChange={(value) => setRequestReasonOther(value)}
                                    disabled={requestReasonType !== 'OTHER'}
                                />
                            </div>
                        </div>

                        {/* 変更後店名 */}
                        <div className="col-lg-3 mb-3 okc-result-form__title">
                            <p>{t('survey-history.request-apply-competitor-store-modal.new-store-name.label')}</p>
                        </div>
                        <div className="col-lg-8 offset-lg-1 mb-3 flex-column">
                            <div className="row w-100">
                                <div className="col-12">{competitorChainName}</div>
                                <Input
                                    placeholder={t('survey-history.request-apply-competitor-store-modal.new-store-name.label')}
                                    maxLength="30"
                                    value={newStoreName}
                                    onChange={(value) => setNewStoreName(value)}
                                    disabled={requestReasonType !== 'RENAME'}
                                />
                            </div>
                        </div>

                        {/* 反映希望日 */}
                        <div className="col-lg-3 mb-3 okc-result-form__title">
                            <p>
                                {t('survey-history.request-apply-competitor-store-modal.preferred-date.label')}
                                <RequiredBadge />
                            </p>
                        </div>
                        <div className="col-lg-8 offset-lg-1 mb-3">
                            <ReactCalender
                                minDate={new Date()}
                                classNameInputDateTime="form-control--input-max-120"
                                value={preferredDate}
                                placeholder={t('survey-history.request-apply-competitor-store-modal.preferred-date.label')}
                                onChange={(value) => setPreferredDate(value)}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        </Modal>
    );
};
export default ModalRequestApplyCompetitorStore;
